/**
 * 批处理可视化对话框（DESIGN.md §2.6 补充，参照场景化入口+模板系统那次
 * 产品评估里"批处理可视化"这一项）。
 *
 * 范围明确限定为**只读流程图 + 输出参数可选**：`action.steps` 是固定的
 * 已录制序列，这里只把它渲染成一列卡片让用户在跑之前看清会发生什么，
 * 不支持拖拽重排、不支持编辑单个步骤的参数——`RecordedStep.args` 没有
 * 跟 `kind` 绑定的强类型 schema，做成可编辑接近"重新做一个 workflow
 * 引擎"，且步骤顺序可能有隐含依赖，随意重排语义上就是错的。要改动作
 * 本身，用户回动作面板重新录制。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { RecordedAction } from '../../app/actions/action-recorder';
import type { BatchFileResult, BatchOptions } from '../../app/batch';
import type { ImageFormat } from '../../io/codecs';
import type { OpenedFile } from '../../io/gateway/types';
import styles from './BatchDialog.module.css';

/** `RecordedStep.kind` → 人类可读文案，覆盖 `action-recorder.ts` 的 `DECODERS` 表全部种类。 */
const STEP_LABELS: Readonly<Record<string, string>> = {
  'canvas.setSize': '修改画布尺寸',
  'adjustment.set': '设置调整图层参数',
  'layer.rename': '重命名图层',
  'layer.visible': '切换图层可见性',
  'layer.opacity': '设置图层不透明度',
  'layer.blendMode': '设置混合模式',
  'layer.transform': '变换图层',
  'layer.effects': '设置图层样式',
  'mask.remove': '移除蒙版',
  'mask.enable': '切换蒙版启用',
  'layer.clip': '切换剪贴蒙版',
  'mask.flipBase': '反转蒙版基底',
  'text.set': '编辑文字内容',
  'text.writingMode': '切换排版方向',
  'shape.setPath': '编辑形状路径',
  'layerComp.add': '新建图层复合',
  'layerComp.remove': '删除图层复合',
  'layerComp.apply': '应用图层复合',
  'smartObject.setFilters': '设置智能滤镜',
};

function stepLabel(kind: string): string {
  return STEP_LABELS[kind] ?? kind;
}

type Stage =
  | { readonly kind: 'pickAction' }
  | { readonly kind: 'pickFiles' }
  | { readonly kind: 'configure' }
  | { readonly kind: 'running' }
  | { readonly kind: 'done' };

export interface BatchDialogProps {
  readonly open: boolean;
  /** 从动作面板某一行进入时预选的动作；从空状态"批量处理图片"卡片进入时是 null。 */
  readonly action: RecordedAction | null;
  readonly availableActions: readonly RecordedAction[];
  readonly onClose: () => void;
  readonly onOpenFiles: (accept: readonly string[]) => Promise<readonly OpenedFile[]>;
  readonly onRun: (
    files: readonly OpenedFile[],
    options: BatchOptions,
    onProgress: (done: number, total: number, current: BatchFileResult | null) => void,
  ) => Promise<readonly BatchFileResult[]>;
}

const ACCEPT = ['.png', '.jpg', '.jpeg'] as const;

export function BatchDialog(props: BatchDialogProps): JSX.Element {
  const [selectedAction, setSelectedAction] = createSignal<RecordedAction | null>(null);
  const [files, setFiles] = createSignal<readonly OpenedFile[]>([]);
  const [stage, setStage] = createSignal<Stage>({ kind: 'pickAction' });
  const [outputFormat, setOutputFormat] = createSignal<ImageFormat>('png');
  const [quality, setQuality] = createSignal(90);
  const [progressDone, setProgressDone] = createSignal(0);
  const [progressTotal, setProgressTotal] = createSignal(0);
  const [results, setResults] = createSignal<readonly BatchFileResult[]>([]);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 组件实例贯穿应用生命周期——每次打开都重置回起点，不留上一次的残留状态
  createEffect(() => {
    if (props.open) {
      setSelectedAction(props.action);
      setFiles([]);
      setOutputFormat('png');
      setQuality(90);
      setProgressDone(0);
      setProgressTotal(0);
      setResults([]);
      setStage({ kind: props.action !== null ? 'pickFiles' : 'pickAction' });
    }
  });

  const pickFiles = (): void => {
    void (async () => {
      const opened = await props.onOpenFiles(ACCEPT);
      if (opened.length === 0) return; // 用户取消
      setFiles(opened);
      setStage({ kind: 'configure' });
    })();
  };

  const run = (): void => {
    const action = selectedAction();
    if (action === null) return;
    setStage({ kind: 'running' });
    setProgressDone(0);
    setProgressTotal(files().length);
    setResults([]);
    const options: BatchOptions = {
      action,
      outputFormat: outputFormat(),
      ...(outputFormat() === 'jpeg' ? { quality: quality() } : {}),
    };
    void props
      .onRun(files(), options, (done, total, current) => {
        setProgressDone(done);
        setProgressTotal(total);
        if (current !== null) setResults((list) => [...list, current]);
      })
      .then((finalResults) => {
        setResults(finalResults);
        setStage({ kind: 'done' });
      });
  };

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="batch-dialog-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="批处理"
          data-testid="batch-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>批处理</h2>

          <Show when={stage().kind === 'pickAction'}>
            <p class={styles.hint}>选一个已录制的动作作为批处理内容：</p>
            <Show
              when={props.availableActions.length > 0}
              fallback={<p class={styles.hint}>还没有录制过任何动作，先去动作面板录制一个。</p>}
            >
              <div class={styles.actionList}>
                <For each={props.availableActions}>
                  {(action) => (
                    <button
                      type="button"
                      class={styles.actionItem}
                      data-testid={`batch-dialog-pick-action-${action.name}`}
                      onClick={() => {
                        setSelectedAction(action);
                        setStage({ kind: 'pickFiles' });
                      }}
                    >
                      {action.name}（{action.steps.length} 步）
                    </button>
                  )}
                </For>
              </div>
            </Show>
          </Show>

          <Show when={stage().kind === 'pickFiles'}>
            <p class={styles.hint}>动作「{selectedAction()?.name}」已选定，选择要处理的图片文件：</p>
            <button type="button" class={styles.primary} data-testid="batch-dialog-pick-files" onClick={pickFiles}>
              选择文件…
            </button>
          </Show>

          <Show when={stage().kind === 'configure' && selectedAction() !== null}>
            <p class={styles.hint}>
              将对 {files().length} 个文件依次执行以下步骤（只读，改动作请回动作面板重新录制）：
            </p>
            <div class={styles.flowchart} data-testid="batch-dialog-flowchart">
              <For each={selectedAction()?.steps ?? []}>
                {(step, index) => (
                  <>
                    <Show when={index() > 0}>
                      <div class={styles.flowArrow} aria-hidden="true" />
                    </Show>
                    <div class={styles.flowStep} data-testid="batch-dialog-flow-step">
                      {stepLabel(step.kind)}
                    </div>
                  </>
                )}
              </For>
            </div>

            <label class={styles.row}>
              <span class={styles.label}>输出格式</span>
              <select
                class={styles.select}
                value={outputFormat()}
                data-testid="batch-dialog-format"
                onChange={(e) => setOutputFormat(e.currentTarget.value as ImageFormat)}
              >
                <option value="png">PNG</option>
                <option value="jpeg">JPEG</option>
              </select>
            </label>

            <Show when={outputFormat() === 'jpeg'}>
              <label class={styles.row}>
                <span class={styles.label}>质量</span>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={quality()}
                  data-testid="batch-dialog-quality"
                  onInput={(e) => setQuality(Number(e.currentTarget.value))}
                />
                <span class={styles.value}>{quality()}</span>
              </label>
            </Show>

            <div class={styles.actions}>
              <button
                type="button"
                class={styles.secondary}
                onClick={() => setStage({ kind: 'pickFiles' })}
              >
                上一步
              </button>
              <button type="button" class={styles.primary} data-testid="batch-dialog-run" onClick={run}>
                开始处理
              </button>
            </div>
          </Show>

          <Show when={stage().kind === 'running'}>
            <p class={styles.hint} data-testid="batch-dialog-progress">
              处理中：{progressDone()} / {progressTotal()}
            </p>
            <progress
              class={styles.progressBar}
              value={progressDone()}
              max={Math.max(1, progressTotal())}
            />
            <ul class={styles.resultList}>
              <For each={results()}>
                {(result) => (
                  <li class={result.ok ? styles.resultOk : styles.resultFail}>
                    {result.ok ? '✓' : '✗'} {result.name}
                    {result.error !== undefined ? ` — ${result.error}` : ''}
                  </li>
                )}
              </For>
            </ul>
          </Show>

          <Show when={stage().kind === 'done'}>
            {(() => {
              const list = results();
              const failed = list.filter((r) => !r.ok).length;
              return (
                <>
                  <p class={styles.hint} data-testid="batch-dialog-summary">
                    {failed === 0
                      ? `全部完成：${list.length} 个文件全部成功`
                      : `处理完成：${list.length - failed}/${list.length} 成功`}
                  </p>
                  <ul class={styles.resultList}>
                    <For each={list}>
                      {(result) => (
                        <li class={result.ok ? styles.resultOk : styles.resultFail}>
                          {result.ok ? '✓' : '✗'} {result.name}
                          {result.error !== undefined ? ` — ${result.error}` : ''}
                        </li>
                      )}
                    </For>
                  </ul>
                  <div class={styles.actions}>
                    <button type="button" class={styles.primary} data-testid="batch-dialog-done-close" onClick={props.onClose}>
                      完成
                    </button>
                  </div>
                </>
              );
            })()}
          </Show>

          <Show when={stage().kind === 'pickAction' || stage().kind === 'pickFiles'}>
            <div class={styles.actions}>
              <button type="button" class={styles.secondary} onClick={props.onClose}>
                取消
              </button>
            </div>
          </Show>
        </div>
      </div>
    </Show>
  );
}
